import { errorLogger } from './errorLogger';

class GlobalErrorHandler {
  private isInitialized = false;

  // Known third-party / extension error patterns to ignore
  private readonly ignoredPatterns = [
    '@context',           // Browser extensions parsing JSON-LD
    'ResizeObserver loop', // Benign browser warning
    'extensions/',        // Browser extension errors
    'chrome-extension://',
    'moz-extension://',
    'EmptyRanges',        // WebKit native media-controls internals (Safari <video>)
    'syncControl',        // WebKit native media-controls internals (Safari <video>)
  ];

  private shouldIgnore(message: string): boolean {
    return this.ignoredPatterns.some(p => message.includes(p));
  }

  /**
   * Cross-origin scripts without CORS headers surface as a blanked-out
   * "Script error." with no message, file, line, or stack. Such an event
   * carries zero diagnostic value, so it is kept out of the persistent log
   * and surfaced in the console only.
   */
  private isOpaqueCrossOriginError(event: ErrorEvent): boolean {
    const message = (event.message || '').trim();
    const isBlankMessage = message === 'Script error.' || message === 'Script error';
    return isBlankMessage && !event.error && !event.filename && !event.lineno;
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      // Resource-load failures are handled by the capture-phase listener below
      if (event.target && event.target !== window) return;

      if (this.isOpaqueCrossOriginError(event)) {
        console.warn('Ignoring opaque cross-origin "Script error." (no diagnostic data)');
        return;
      }

      const msg = event.error?.message || event.message || '';
      if (this.shouldIgnore(msg)) return;
      console.error('Global error caught:', event.error);
      errorLogger.logJavaScriptError(event);
    });


    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const msg = event.reason?.message || String(event.reason || '');
      if (this.shouldIgnore(msg)) return;
      console.error('Unhandled promise rejection:', event.reason);
      errorLogger.logPromiseRejection(event);
    });

    // Handle resource loading errors (scripts, stylesheets).
    // Media assets (img/video/audio/source) fail routinely on stale caches and
    // flaky mobile networks — they degrade gracefully, so they stay out of the
    // error log and are only surfaced in the console.
    const QUIET_RESOURCE_TAGS = new Set(['IMG', 'VIDEO', 'AUDIO', 'SOURCE', 'TRACK', 'PICTURE']);
    window.addEventListener('error', (event) => {
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        const source = (target as any).src || (target as any).href;

        if (QUIET_RESOURCE_TAGS.has(target.tagName)) {
          console.warn('Media resource failed to load:', target.tagName, source);
          return;
        }

        console.error('Resource loading error:', {
          type: target.tagName,
          source,
          error: event.error
        });

        errorLogger.logError({
          error_type: 'javascript_error',
          error_message: `Failed to load ${target.tagName}: ${source}`,
          severity: 'low',
          additional_data: {
            resourceType: target.tagName,
            source,
            resourceLoadingError: true
          }
        });
      }
    }, true); // Use capture phase for resource errors

    this.isInitialized = true;
    console.log('Global error handler initialized');
  }

  // Method to manually log errors
  logError(error: Error, context?: string) {
    errorLogger.logError({
      error_type: 'javascript_error',
      error_message: error.message,
      error_stack: error.stack,
      severity: 'medium',
      additional_data: {
        context,
        manual: true
      }
    });
  }

  // Method to wrap async functions with error handling
  wrapAsync<T extends (...args: any[]) => Promise<any>>(fn: T, context?: string): T {
    return ((...args: any[]) => {
      return fn(...args).catch((error) => {
        console.error(`Async error in ${context || 'unknown context'}:`, error);
        this.logError(error, context);
        throw error; // Re-throw to maintain normal error flow
      });
    }) as T;
  }

  // Method to wrap functions with error handling
  wrap<T extends (...args: any[]) => any>(fn: T, context?: string): T {
    return ((...args: any[]) => {
      try {
        const result = fn(...args);
        
        // Handle promises returned from the function
        if (result && typeof result.catch === 'function') {
          return result.catch((error: Error) => {
            console.error(`Promise error in ${context || 'unknown context'}:`, error);
            this.logError(error, context);
            throw error;
          });
        }
        
        return result;
      } catch (error) {
        console.error(`Error in ${context || 'unknown context'}:`, error);
        this.logError(error as Error, context);
        throw error; // Re-throw to maintain normal error flow
      }
    }) as T;
  }
}

export const globalErrorHandler = new GlobalErrorHandler();